import { Meeting } from '../services/meetingService';
import { isWorkshopStarted } from './workshopPhase';

export interface WorkshopCalendar {
  current_step?: number | null;
  step_title?: string;
  step_started_date?: string;
  where_we_are?: string;
  likely_complete?: string;
  next_step_starts?: string;
  published?: boolean;
}

export interface WorkshopUpdate {
  id: string;
  date: string;
  body: string;
}

export const EMPTY_WORKSHOP_CALENDAR: WorkshopCalendar = {
  current_step: null,
  step_title: '',
  step_started_date: '',
  where_we_are: '',
  likely_complete: '',
  next_step_starts: '',
  published: false,
};

export function parseWorkshopCalendar(meeting: Meeting | null): WorkshopCalendar {
  const raw = meeting?.workshop_calendar;
  if (!raw || typeof raw !== 'object') return { ...EMPTY_WORKSHOP_CALENDAR };
  return {
    current_step: raw.current_step ?? null,
    step_title: raw.step_title || '',
    step_started_date: raw.step_started_date || '',
    where_we_are: raw.where_we_are || '',
    likely_complete: raw.likely_complete || '',
    next_step_starts: raw.next_step_starts || '',
    published: !!raw.published,
  };
}

export function parseWorkshopUpdates(meeting: Meeting | null): WorkshopUpdate[] {
  const raw = meeting?.workshop_updates;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((u) => u && typeof u === 'object' && String(u.body || '').trim())
    .map((u) => ({
      id: String(u.id || ''),
      date: String(u.date || ''),
      body: String(u.body || '').trim(),
    }))
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
}

export function calendarHasContent(cal: WorkshopCalendar): boolean {
  return !!(
    (cal.current_step != null && cal.current_step >= 0) ||
    cal.step_title?.trim() ||
    cal.step_started_date ||
    cal.where_we_are?.trim() ||
    cal.likely_complete?.trim()
  );
}

export function formatCalendarStepLabel(cal: WorkshopCalendar): string | null {
  if (cal.current_step === 0) {
    return cal.step_title?.trim() ? `Start — ${cal.step_title.trim()}` : 'Start';
  }
  if (cal.current_step != null && cal.current_step >= 1) {
    return `Step ${cal.current_step}${cal.step_title?.trim() ? ` — ${cal.step_title.trim()}` : ''}`;
  }
  return cal.step_title?.trim() || null;
}

export function shouldShowWorkshopCalendar(meeting: Meeting | null): boolean {
  const cal = parseWorkshopCalendar(meeting);
  if (!calendarHasContent(cal)) return false;
  if (cal.published) return true;
  return isWorkshopStarted(meeting);
}

export function formatCalendarDateLabel(value: string | undefined): string {
  if (!value?.trim()) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    try {
      const d = new Date(`${value}T12:00:00`);
      return d.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return value;
    }
  }
  return value;
}

export function formatUpdateDateLabel(isoDate: string): string {
  if (!isoDate) return '';
  try {
    const d = new Date(`${isoDate}T12:00:00`);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return isoDate;
  }
}
